<template>
  <section id="features" class="bg-white py-24 sm:py-32" aria-labelledby="features-heading" data-section-name="Features">
    <div class="mx-auto max-w-7xl px-6 lg:px-8">
      <header class="mx-auto max-w-2xl lg:text-center">
        <p class="text-base font-semibold leading-7 text-gray-900">Fast & Reliable</p>
        <h2 id="features-heading" class="mt-2 text-3xl font-bold tracking-tight text-black sm:text-4xl">Everything you need for PDF generation</h2>
        <p class="mt-6 text-lg leading-8 text-gray-600">Our API is designed for speed, reliability, and ease of use. Convert HTML or URLs to high-quality PDFs in seconds with real-time job tracking.</p>
      </header>
      <div class="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-4xl">
        <dl class="grid max-w-xl grid-cols-1 gap-x-8 gap-y-10 lg:max-w-none lg:grid-cols-2 lg:gap-y-16">
          <div v-for="feature in features" :key="feature.name" class="relative pl-16">
            <dt class="text-base font-semibold leading-7 text-black">
              <div class="absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-lg bg-black">
                <component :is="feature.icon" class="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              {{ feature.name }}
            </dt>
            <dd class="mt-2 text-base leading-7 text-gray-600">{{ feature.description }}</dd>
          </div>
        </dl>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { h } from 'vue'

const features = [
  {
    name: 'HTML & URL Input',
    description: 'Submit raw HTML content or public URLs. Our API handles both input types seamlessly and converts them to clean PDFs.',
    icon: () => h('svg', { fill: 'none', viewBox: '0 0 24 24', 'stroke-width': '1.5', stroke: 'currentColor' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', d: 'M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5' })
    ])
  },
  {
    name: 'Real-time Tracking',
    description: 'Monitor your PDF generation jobs in real-time with our status queue system. Know exactly when your PDF is ready.',
    icon: () => h('svg', { fill: 'none', viewBox: '0 0 24 24', 'stroke-width': '1.5', stroke: 'currentColor' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', d: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z' })
    ])
  },
  {
    name: 'High-Quality Output',
    description: 'Generate clean, professional PDFs perfect for reports, invoices, receipts, and business documents.',
    icon: () => h('svg', { fill: 'none', viewBox: '0 0 24 24', 'stroke-width': '1.5', stroke: 'currentColor' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', d: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z' })
    ])
  },
  {
    name: 'Developer-Friendly API',
    description: 'Simple REST API designed for easy integration into any application or workflow. Get started in minutes with comprehensive documentation.',
    icon: () => h('svg', { fill: 'none', viewBox: '0 0 24 24', 'stroke-width': '1.5', stroke: 'currentColor' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', d: 'M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z' })
    ])
  },
]
</script>
